import type { ConversationPlacement, CrowdexAssistant } from '@crowdex/agent-contracts/api';

/**
 * A child conversation (design D49, mobile 47): a whole conversation attached to a parent (the
 * project's conversation for a workflow's task, the conversation that delegated it otherwise).
 * What its header and its first message say of it.
 */
export type ChildConversation = {
  parentId: string;
  /** The parent's name: its project's, or its title. */
  parentTitle: string;
  /** The parent's project colour, when the parent is a project's conversation. */
  parentColor: string | null;
  /** "Task 2 of 5 · Careful · Develop" for a workflow's task; no workflow for an agent's alone. */
  task: { index: number; count: number; workflow: string | null; step: string | null } | null;
};

/** "Task 2 of 5 · Careful · Develop". */
export function taskChipLabel(task: NonNullable<ChildConversation['task']>): string {
  return [`Task ${task.index} of ${task.count}`, task.workflow, task.step]
    .filter((part): part is string => part !== null && part !== '')
    .join(' · ');
}

/** "From Crowdex Mobile · Careful automation", above the task the child was given. */
export function originLabel(child: ChildConversation): string {
  return child.task?.workflow == null
    ? `From ${child.parentTitle}`
    : `From ${child.parentTitle} · ${child.task.workflow} automation`;
}

/**
 * The child conversation `conversationId` is, from the assistant directory's placements; null
 * when it has no parent. Its task's number counts the parent's tasks in the directory's order.
 */
export function childConversation(input: {
  conversationId: string;
  placements: Readonly<Record<string, ConversationPlacement>>;
  projects: readonly CrowdexAssistant[];
  /** Titles of the conversations the shell knows. */
  titleOf: (conversationId: string) => string | null;
  /** The parent's tasks in the order they started (the project's Tasks), when read. */
  taskOrder?: readonly string[] | undefined;
}): ChildConversation | null {
  const placement = input.placements[input.conversationId];
  const parentId = placement?.parentConversationId ?? null;
  if (placement === undefined || parentId === null) return null;
  const project = input.projects.find((candidate) => candidate.conversationId === parentId);
  const run = placement.workflowRun;
  let task: ChildConversation['task'] = null;
  if (run !== null) {
    const siblings = Object.entries(input.placements).filter(
      ([, entry]) => entry.parentConversationId === parentId && entry.workflowRun !== null,
    );
    // Its automation, by its name when it started.
    const workflow = run.workflowId === null ? null : (run.automationName ?? run.workflowId);
    // In the order the tasks started when the project's Tasks are read, else as listed.
    const order = input.taskOrder?.includes(input.conversationId)
      ? input.taskOrder
      : siblings.map(([id]) => id);
    task = {
      index: order.indexOf(input.conversationId) + 1,
      count: order.length,
      workflow,
      step: run.step,
    };
  }
  return {
    parentId,
    parentTitle: project?.name ?? input.titleOf(parentId) ?? 'Parent conversation',
    parentColor: project?.color ?? null,
    task,
  };
}
